import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { ScrollArea } from './ui/scroll-area';
import { motion } from 'framer-motion';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  return (
    <motion.header 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 
        ${scrolled ? 'bg-background/90 backdrop-blur-md shadow-md' : 'bg-transparent'}`}
    >
      <div className="container mx-auto px-4 py-4">
        <nav className="flex items-center justify-between">
          <motion.div 
            className="flex items-center"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            <NavLink to="/" className="flex items-center gap-3 group">
              <div className="relative w-12 h-12 overflow-hidden rounded-xl 
                            transition-transform duration-300 group-hover:scale-110
                            shadow-lg shadow-tech-blue/20">
                <img src="/lovable-uploads/ee07814f-f684-4628-bfae-cd006032915f.png" 
                     alt="V3D Studio Logo" 
                     className="w-full h-full object-cover transform transition-transform duration-500 group-hover:rotate-6" />
                <div className="absolute inset-0 bg-gradient-to-tr from-tech-blue/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div className="flex flex-col items-start">
                <span className="text-2xl font-playfair font-bold bg-gradient-to-r from-tech-blue to-purple-400 bg-clip-text text-transparent">
                  V3D STUDIO
                </span>
                <span className="text-sm text-foreground/60 font-medium tracking-wide">
                  Precision Engineering
                </span>
              </div>
            </NavLink>
          </motion.div>

          {/* Desktop Navigation */}
          <ul className="hidden md:flex items-center space-x-8">
            <motion.li whileHover={{ scale: 1.1 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
              <NavLink to="/" className={({isActive}) => 
                `nav-link font-playfair text-lg ${isActive ? 'active' : ''} hover:text-tech-blue transition-all duration-300`} end>
                Úvod
              </NavLink>
            </motion.li>
            <motion.li whileHover={{ scale: 1.1 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
              <NavLink to="/about" className={({isActive}) => 
                `nav-link ${isActive ? 'active' : ''} hover:text-tech-blue transition-all duration-300`}>
                O projektu
              </NavLink>
            </motion.li>
            <motion.li whileHover={{ scale: 1.1 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
              <NavLink to="/technical-details" className={({isActive}) => 
                `nav-link ${isActive ? 'active' : ''} hover:text-tech-blue transition-all duration-300`}>
                Technické detaily
              </NavLink>
            </motion.li>
            <motion.li whileHover={{ scale: 1.1 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
              <NavLink to="/downloads" className={({isActive}) => 
                `nav-link ${isActive ? 'active' : ''} hover:text-tech-blue transition-all duration-300`}>
                Ke stažení
              </NavLink>
            </motion.li>
            <motion.li whileHover={{ scale: 1.1 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
              <NavLink to="/contact" className={({isActive}) => 
                `nav-link ${isActive ? 'active' : ''} hover:text-tech-blue transition-all duration-300`}>
                Kontakt
              </NavLink>
            </motion.li>
            <motion.li whileHover={{ scale: 1.1 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
              <ThemeToggle />
            </motion.li>
          </ul>

          <div className="md:hidden flex items-center gap-2">
            <ThemeToggle />
            <motion.button 
              whileTap={{ scale: 0.95 }}
              className="text-foreground hover:text-tech-blue transition-colors" 
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.button>
          </div>
        </nav>
      </div>

      <motion.div 
        className={`md:hidden fixed inset-0 top-[68px] z-50 ${isOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
        initial={{ opacity: 0, x: -300 }}
        animate={{ 
          opacity: isOpen ? 1 : 0,
          x: isOpen ? 0 : -300
        }}
        transition={{ duration: 0.3 }}
      >
        <ScrollArea className="h-full bg-background/95 backdrop-blur-md shadow-lg">
          <ul className="py-4 px-6 space-y-4">
            <motion.li whileHover={{ x: 10 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
              <NavLink 
                to="/" 
                className={({isActive}) => `block nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setIsOpen(false)}
                end
              >
                Úvod
              </NavLink>
            </motion.li>
            <motion.li whileHover={{ x: 10 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
              <NavLink 
                to="/about" 
                className={({isActive}) => `block nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setIsOpen(false)}
              >
                O projektu
              </NavLink>
            </motion.li>
            <motion.li whileHover={{ x: 10 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
              <NavLink 
                to="/technical-details" 
                className={({isActive}) => `block nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setIsOpen(false)}
              >
                Technické detaily
              </NavLink>
            </motion.li>
            <motion.li whileHover={{ x: 10 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
              <NavLink 
                to="/downloads" 
                className={({isActive}) => `block nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setIsOpen(false)}
              >
                Ke stažení
              </NavLink>
            </motion.li>
            <motion.li whileHover={{ x: 10 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
              <NavLink 
                to="/contact" 
                className={({isActive}) => `block nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setIsOpen(false)}
              >
                Kontakt
              </NavLink>
            </motion.li>
          </ul>
        </ScrollArea>
      </motion.div>
    </motion.header>
  );
};

export default Navigation;
